import { useCallback } from 'react';
import { useCreateFunnelPage, useUpdateFunnelPage } from '../queries/funnel-page-mutations';
import { useFunnelEditorStore } from '../stores/funnelEditorStore';
import { cleanRouting } from '../logic';
import { DEFAULT_TOP_BANNER_TEXT, DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';
import type { FunnelPageItem, FunnelPageRouting } from '../types';

export interface PublishFunnelPagePayload {
    pageId: number;
    referencePageId: number;
    productTitle: string;
    order: number;
    rawOrder?: number;
    topBannerText?: string;
    /** Pass null to clear an existing override. */
    funnelPrice?: number | null;
    funnelCtaText?: string;
    /** null clears routing; undefined leaves server-side value untouched. */
    routing?: FunnelPageRouting | null;
}

interface UseFunnelPublishPageArgs {
    storeId: number | undefined;
    savedFunnelPages: FunnelPageItem[];
    maybeAutoRenameFunnel: (order: number, productTitle: string) => void;
}

/**
 * Publishes (or updates) a single funnel page. Draft pages create; existing
 * pages update. Routing pointing at deleted pages is cleaned before the
 * request. On create, selection follows the new page id (or falls back to
 * `pendingSelectOrder` if the API doesn't echo the id).
 */
export function useFunnelPublishPage({ storeId, savedFunnelPages, maybeAutoRenameFunnel }: UseFunnelPublishPageArgs) {
    const createFunnelPage = useCreateFunnelPage(storeId ?? 0);
    const updateFunnelPage = useUpdateFunnelPage(storeId ?? 0, true);
    const setHasDraftPage = useFunnelEditorStore(s => s.setHasDraftPage);
    const setSelectedSuggestedIndex = useFunnelEditorStore(s => s.setSelectedSuggestedIndex);
    const setSelectedPageId = useFunnelEditorStore(s => s.setSelectedPageId);
    const setPendingSelectOrder = useFunnelEditorStore(s => s.setPendingSelectOrder);

    const handlePublishPage = useCallback(
        (payload: PublishFunnelPagePayload) => {
            if (!storeId) return;

            // Strip any routing pointing at deleted pages before we hit the server.
            const cleanedRouting =
                payload.routing === undefined ? undefined : cleanRouting(payload.routing, savedFunnelPages);

            if (payload.pageId === DRAFT_FUNNEL_PAGE_ID) {
                // Entry page (order 1) never gets the top banner; every other slot does.
                const bannerText = payload.order === 1 ? undefined : (payload.topBannerText ?? DEFAULT_TOP_BANNER_TEXT);
                createFunnelPage.mutate(
                    {
                        product: {
                            type: 'funnel-page',
                            title: payload.productTitle,
                            funnel_page: { reference_page_id: payload.referencePageId },
                            ...(typeof payload.funnelPrice === 'number' && {
                                funnel_price: payload.funnelPrice,
                            }),
                            ...(payload.funnelCtaText &&
                                payload.funnelCtaText.length > 0 && {
                                    funnel_cta_text: payload.funnelCtaText,
                                }),
                        },
                        top_banner_text: bannerText,
                        order: payload.rawOrder ?? payload.order,
                        ...(cleanedRouting !== undefined && { routing: cleanedRouting }),
                    },

                    {
                        onSuccess: response => {
                            const newPageId = (response as { page?: { page_id?: number } } | undefined)?.page?.page_id;
                            setHasDraftPage(false);
                            setSelectedSuggestedIndex(null);
                            if (newPageId) {
                                setSelectedPageId(newPageId);
                            } else {
                                // Backend didn't echo the new page_id — fall back to
                                // picking it up by `order` once the refetch lands.
                                setPendingSelectOrder(payload.order);
                            }
                            maybeAutoRenameFunnel(payload.order, payload.productTitle);
                        },
                    }
                );
                return;
            }

            updateFunnelPage.mutate(
                {
                    page_id: payload.pageId,
                    referencePageId: payload.referencePageId,
                    productTitle: payload.productTitle,
                    order: payload.order,
                    publish: true,
                    topBannerText: payload.topBannerText,
                    funnelPrice: payload.funnelPrice,
                    funnelCtaText: payload.funnelCtaText,
                    ...(cleanedRouting !== undefined && { routing: cleanedRouting }),
                },
                {
                    onSuccess: () => {
                        maybeAutoRenameFunnel(payload.order, payload.productTitle);
                    },
                }
            );
        },
        [
            storeId,
            savedFunnelPages,
            createFunnelPage,
            updateFunnelPage,
            maybeAutoRenameFunnel,
            setHasDraftPage,
            setSelectedSuggestedIndex,
            setSelectedPageId,
            setPendingSelectOrder,
        ]
    );

    return {
        handlePublishPage,
        isPublishing: createFunnelPage.isPending || updateFunnelPage.isPending,
    };
}
